/**
 * Date-range presets for the shared admin Filters experience.
 *
 * App timezone convention is America/New_York (see ClientDetailPage
 * timestamps) — every boundary here is ET wall-clock converted to an
 * exact UTC instant, so the API can compare plain ISO strings against
 * timestamptz columns without timezone logic of its own.
 *
 * All helpers are pure: `now` is injectable for deterministic tests,
 * invalid input returns null (never throws).
 *
 * Presets: 1D = trailing 24h, 1W = trailing 7d, 1M = trailing 30d,
 * YTD = Jan 1 (current ET year) 00:00 ET → now,
 * CUSTOM = selected start 00:00:00.000 ET → selected end 23:59:59.999 ET
 * (both inclusive).
 */

const ET_TIME_ZONE = 'America/New_York';
const HOUR_MS = 60 * 60 * 1000;

const TRAILING_HOURS = { '1D': 24, '1W': 24 * 7, '1M': 24 * 30 };

export const DATE_PRESET_KEYS = ['1D', '1W', '1M', 'YTD', 'CUSTOM'];

export const DATE_PRESET_LABELS = {
  '1D': 'Last 24 hours',
  '1W': 'Last 7 days',
  '1M': 'Last 30 days',
  YTD: 'Year to date',
  CUSTOM: 'Custom range'
};

const etPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ET_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

// ET wall-clock parts of a UTC instant
const getEtParts = (date) =>
  etPartsFormatter.formatToParts(date).reduce(
    (acc, { type, value }) =>
      type === 'literal' ? acc : { ...acc, [type]: Number(value) },
    {}
  );

// Offset (ms) between ET wall clock and UTC at a given instant.
// Compared at whole-second precision — the formatter drops milliseconds.
const getEtOffsetMs = (instantMs) => {
  const parts = getEtParts(new Date(instantMs));
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return wallAsUtc - Math.floor(instantMs / 1000) * 1000;
};

// UTC instant for an ET wall-clock time. Two-pass conversion: the first
// offset guess can be wrong within ~5h of a DST transition; probing again
// at the corrected instant settles it deterministically.
const etWallClockToUtc = ({
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0
}) => {
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const firstPass = wallAsUtc - getEtOffsetMs(wallAsUtc);
  return new Date(wallAsUtc - getEtOffsetMs(firstPass));
};

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// "YYYY-MM-DD" → { year, month, day } for real calendar dates, else null
const parseYmd = (value) => {
  const match = YMD_PATTERN.exec(String(value ?? ''));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const roundTrip = new Date(Date.UTC(year, month - 1, day));
  const isRealDate =
    roundTrip.getUTCFullYear() === year &&
    roundTrip.getUTCMonth() === month - 1 &&
    roundTrip.getUTCDate() === day;
  return isRealDate ? { year, month, day } : null;
};

// { from, to } UTC ISO strings for a non-custom preset, else null
export const getPresetRange = (preset, now = new Date()) => {
  const key = String(preset ?? '').toUpperCase();
  const trailingHours = TRAILING_HOURS[key];
  if (trailingHours) {
    return {
      from: new Date(now.getTime() - trailingHours * HOUR_MS).toISOString(),
      to: now.toISOString()
    };
  }
  if (key === 'YTD') {
    const { year } = getEtParts(now);
    return {
      from: etWallClockToUtc({ year, month: 1, day: 1 }).toISOString(),
      to: now.toISOString()
    };
  }
  return null;
};

// Inclusive ET-day range: start 00:00:00.000 ET → end 23:59:59.999 ET
export const getCustomRange = (startYmd, endYmd) => {
  const start = parseYmd(startYmd);
  const end = parseYmd(endYmd);
  if (!start || !end) return null;
  const from = etWallClockToUtc(start);
  const to = etWallClockToUtc({
    ...end,
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999
  });
  return from.getTime() <= to.getTime()
    ? { from: from.toISOString(), to: to.toISOString() }
    : null;
};

// Single entry point for a page's date-filter value:
// { preset: '1D'|'1W'|'1M'|'YTD'|'CUSTOM', start?: 'YYYY-MM-DD', end?: 'YYYY-MM-DD' }
export const resolveDateRange = (value, now = new Date()) => {
  if (!value?.preset) return null;
  const key = String(value.preset).toUpperCase();
  return key === 'CUSTOM'
    ? getCustomRange(value.start, value.end)
    : getPresetRange(key, now);
};

// Human label for chips: preset label, or "2026-07-01 – 2026-07-20"
export const describeDateRange = (value) => {
  if (!value?.preset) return null;
  const key = String(value.preset).toUpperCase();
  if (key !== 'CUSTOM') return DATE_PRESET_LABELS[key] ?? null;
  return value.start && value.end ? `${value.start} – ${value.end}` : null;
};
